import {
  allLegalActions,
  applyAction,
  randomInt,
  type GameState,
  type PieceAction,
  type Rng,
} from '@halvard/rules';
import { evaluate, type Weights } from './evaluate.js';
import { searchBest } from './search.js';
import { DIFFICULTIES, STRATEGIES } from './strategies.js';
import type { Bot, BotSpec } from './types.js';

/**
 * The game as the bot knows it: its view, played as if it were the whole board. Under fog, enemy
 * pieces it cannot see are simply absent and unseen squares have the heights it remembers.
 */
export function knownGame(view: GameState): GameState {
  if (!view.config.fog) return view;
  return { ...view, config: { ...view.config, fog: false }, memory: null };
}

/**
 * Chooses an action for the side to move in `view`. Synchronous core used by the worker, the
 * arena and `createBot`. Throws if the side to move has no legal action (the game is over).
 */
export function chooseAction(view: GameState, spec: BotSpec, rng: Rng): PieceAction {
  const game = knownGame(view);
  const me = game.turn;
  const actions = allLegalActions(game);
  if (actions.length === 0) throw new Error('No legal action: the game is over');
  const level = DIFFICULTIES[spec.difficulty];
  const weights = STRATEGIES[spec.strategy].weights;

  if (level.blunder > 0 && rng() < level.blunder) return pick(actions, rng);
  if (level.search) return searchBest(game, weights, spec.blind === true, level.search, rng);

  const blind = spec.blind === true;
  let best = -Infinity;
  const scored: { action: PieceAction; score: number }[] = [];
  for (const action of actions) {
    const next = applyAction(game, action.pieceId, action.action);
    const score =
      level.depth === 2
        ? worstReply(next, me, weights, best - level.margin, blind)
        : evaluate(next, me, weights, blind);
    scored.push({ action, score });
    if (score > best) best = score;
  }
  return pick(
    scored.filter((s) => s.score >= best - level.margin).map((s) => s.action),
    rng,
  );
}

/**
 * The score of `state` after the opponent's best reply. Stops early once a reply drops the score
 * below `floor`: this move can no longer be among the best, and its (upper-bound) score stays
 * below the cut so it is never picked.
 */
function worstReply(
  state: GameState,
  me: GameState['turn'],
  weights: Weights,
  floor: number,
  blind: boolean,
): number {
  if (state.winReason !== null) return evaluate(state, me, weights, blind);
  let worst = Infinity;
  for (const reply of allLegalActions(state)) {
    const score = evaluate(applyAction(state, reply.pieceId, reply.action), me, weights, blind);
    if (score < worst) worst = score;
    if (worst < floor) break;
  }
  return worst;
}

function pick<T>(items: readonly T[], rng: Rng): T {
  const item = items[randomInt(rng, items.length)];
  /* c8 ignore next */
  if (item === undefined) throw new Error('Nothing to pick from');
  return item;
}

export function botName(spec: BotSpec): string {
  const blind = spec.blind ? ' · terrain-blind' : '';
  return `${STRATEGIES[spec.strategy].name} · ${DIFFICULTIES[spec.difficulty].name}${blind}`;
}

export function createBot(spec: BotSpec): Bot {
  return {
    spec,
    name: botName(spec),
    chooseAction: (view, rng) => Promise.resolve(chooseAction(view, spec, rng)),
  };
}
